import { Box, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { ErrorSnackbar } from '../components/ErrorSnackbar';
import { FormExpirationGuard } from '../components/FormExpirationGuard';
import { FormHeaderImage, FormHeaderWrapper, FormLoadingOverlay, FormPaper } from '../components/FormStyledComponents';
import { PrivacyConsentSection } from '../components/PrivacyConsentSection';
import { useHeaderImage } from '../hooks/useHeaderImage';
import { LanscoButton } from '../lansco-button';
import { downloadFile } from '../utils/downloadFile';
import { FORM_IDS } from '../utils/formAssets';
import { submitFormToApi } from '../utils/submitForm';

interface FormValues {
  name: string;
  email: string;
  phone: string;
  company: string;
  sessionId: string;
  consent: boolean;
  website: string;
}

const WEBINAR_SESSIONS = [
  {
    value: '2026-09-22',
    label: '22.09.2026, 10:00 - 10:30 Uhr',
    registrationClosesAt: '2026-09-22T00:00:00+02:00',
    fileName: '2026-09-22_Webinar.ics',
    downloadName: 'Microsoft-365-im-Blindflug-2026-09-22.ics',
  },
  {
    value: '2026-09-24',
    label: '24.09.2026, 14:30 - 15:00 Uhr',
    registrationClosesAt: '2026-09-24T15:00:00+02:00',
    fileName: '2026-09-24_Webinar.ics',
    downloadName: 'Microsoft-365-im-Blindflug-2026-09-24.ics',
  },
];

const validationSchema = Yup.object({
  name: Yup.string().trim().max(255, 'Name darf maximal 255 Zeichen enthalten').required('Name ist erforderlich'),
  email: Yup.string()
    .email('Ungültige E-Mail-Adresse')
    .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 'Ungültiges E-Mail-Format')
    .required('E-Mail ist erforderlich'),
  phone: Yup.string().max(255, 'Telefonnummer darf maximal 255 Zeichen enthalten'),
  company: Yup.string().max(255, 'Unternehmen darf maximal 255 Zeichen enthalten'),
  sessionId: Yup.string().required('Bitte wähle einen Webinar-Termin aus'),
  consent: Yup.boolean()
    .required('Bitte stimme den Datenschutzbestimmungen zu')
    .oneOf([true], 'Du musst den Datenschutzbestimmungen zustimmen, um Dich für das Webinar anzumelden'),
});

const initialValues: FormValues = {
  name: '',
  email: '',
  phone: '',
  company: '',
  sessionId: '',
  consent: false,
  website: '',
};

const API_ENDPOINT = '/api/submit-webinar';

export const M365ImBlindflug = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [selectedSession, setSelectedSession] = useState('');
  const [now, setNow] = useState(Date.now());
  const formMountedAtRef = useRef(Date.now());
  const headerImage = useHeaderImage(FORM_IDS.M365_IM_BLINDFLUG);

  useEffect(() => {
    const nextBoundary = WEBINAR_SESSIONS.map((session) =>
      new Date(session.registrationClosesAt).getTime()
    )
      .filter((boundary) => boundary > now)
      .sort((first, second) => first - second)[0];
    if (!nextBoundary) return;

    const timeout = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(nextBoundary - now + 1000, 2_147_483_647)
    );
    return () => window.clearTimeout(timeout);
  }, [now]);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    setIsLoading(true);
    setErrorMessage('');
    setShowErrorSnackbar(false);

    try {
      const result = await submitFormToApi({
        apiEndpoint: API_ENDPOINT,
        payload: {
          name: values.name,
          mail: values.email,
          phone: values.phone,
          company: values.company,
          sessionId: values.sessionId,
          consent: values.consent,
          website: values.website,
          elapsedMs: Date.now() - formMountedAtRef.current,
        },
        debugFieldValue: values.name,
      });

      if (result.success) {
        setSelectedSession(values.sessionId);
        setIsSubmitted(true);
      } else {
        setErrorMessage(result.error!);
        setShowErrorSnackbar(true);
      }
    } catch (error) {
      console.error('Fehler beim Absenden des Webinarformulars:', error);
      setErrorMessage('Beim Absenden des Formulars ist ein Fehler aufgetreten. Bitte versuche es erneut.');
      setShowErrorSnackbar(true);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    const session = WEBINAR_SESSIONS.find((option) => option.value === selectedSession);
    if (!session) return;

    downloadFile({
      fileUrl: `${window.location.origin}/forms/${FORM_IDS.M365_IM_BLINDFLUG}/${session.fileName}`,
      fileName: session.downloadName,
      mimeType: 'text/calendar',
      redirectUrl: 'https://www.lansco.de/',
    });
  };

  return (
    <>
      <FormHeaderWrapper>
        <FormHeaderImage src={headerImage} alt="Microsoft 365 im Blindflug?" />
      </FormHeaderWrapper>
      <FormPaper elevation={3}>
        <FormExpirationGuard
          expirationDate="2026-09-24"
          expirationTime="15:00"
          expiredTitle="Anmeldung geschlossen"
          expiredMessage="Die Anmeldung für das Webinar „Microsoft 365 im Blindflug?“ ist leider nicht mehr möglich."
        >
          {!isSubmitted ? (
            <>
              <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 300, mb: 3, color: 'primary.main' }}>
                Microsoft 365 im Blindflug? – Risiken im Tenant erkennen. Sicherheit gewinnen.
              </Typography>
              <Typography variant="body1" component="p" sx={{ mb: 2, lineHeight: 1.6 }}>
                Wie sicher ist Dein Microsoft-365-Tenant wirklich? In diesem kostenlosen 30-minütigen Webinar erfährst Du, warum einzelne Kennzahlen oder einmalige Prüfungen oft nicht ausreichen und wie sich Risiken strukturiert bewerten und priorisieren lassen.
              </Typography>
              <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                Das erwartet Dich:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ mb: 2, pl: 2, '& li': { mb: 1 } }}>
                <li>Typische Risiken und Schwachstellen in Microsoft 365</li>
                <li>Grenzen einzelner Sicherheitskennzahlen</li>
                <li>Strukturierte Bewertung und Priorisierung von Risiken</li>
                <li>Kritische Veränderungen frühzeitig erkennen</li>
                <li>Einblick in den LANSCO Tenant Security Service</li>
              </Typography>
              <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                Jetzt anmelden!
              </Typography>
              <Typography variant="body1" component="p" sx={{ mb: 2, lineHeight: 1.6 }}>
                Melde Dich einfach mit Deinen Kontaktdaten an und wähle Deinen bevorzugten Webinar-Termin aus. Nach erfolgreicher Anmeldung kannst Du dir den Termin herunterladen und in deinem Kalender speichern.
              </Typography>
              <Typography
                variant="body1"
                component="p"
                sx={{ mb: 3, pl: 2, py: 1, borderLeft: 4, borderColor: 'primary.main', lineHeight: 1.6 }}
              >
                <Box component="strong">Exklusiv für Webinar-Teilnehmende:</Box> Vereinbare im Anschluss ein persönliches Gespräch und erhalte bei einer Beauftragung die <Box component="strong">ersten drei Monate kostenfrei</Box>.
              </Typography>
              {isLoading && (
                <FormLoadingOverlay>
                  <CircularProgress size={60} sx={{ color: 'primary.main' }} />
                </FormLoadingOverlay>
              )}
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnMount
                initialTouched={{ consent: true }}
              >
                {({ errors, touched, isValid, dirty, values, handleChange }) => (
                  <Form>
                    <Box
                      aria-hidden="true"
                      sx={{ position: 'absolute', left: '-5000px', width: 1, height: 1, overflow: 'hidden' }}
                    >
                      <label htmlFor="website">Dieses Feld bitte leer lassen</label>
                      <input
                        id="website"
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={values.website}
                        onChange={handleChange}
                      />
                    </Box>
                    <Field
                      as={TextField}
                      fullWidth
                      margin="normal"
                      name="name"
                      label="Name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      sx={{ mb: 2 }}
                      disabled={isLoading}
                    />
                    <Field
                      as={TextField}
                      fullWidth
                      margin="normal"
                      name="email"
                      label="E-Mail"
                      type="email"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      sx={{ mb: 2 }}
                      disabled={isLoading}
                    />
                    <Field
                      as={TextField}
                      fullWidth
                      margin="normal"
                      name="phone"
                      label="Telefonnummer"
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                      sx={{ mb: 2 }}
                      disabled={isLoading}
                    />
                    <Field
                      as={TextField}
                      fullWidth
                      margin="normal"
                      name="company"
                      label="Unternehmen"
                      error={touched.company && Boolean(errors.company)}
                      helperText={touched.company && errors.company}
                      sx={{ mb: 2 }}
                      disabled={isLoading}
                    />
                    <Field
                      as={TextField}
                      select
                      fullWidth
                      margin="normal"
                      name="sessionId"
                      label="Webinar-Termin auswählen"
                      error={touched.sessionId && Boolean(errors.sessionId)}
                      helperText={touched.sessionId && errors.sessionId}
                      sx={{ mb: 2 }}
                      disabled={isLoading}
                    >
                      {WEBINAR_SESSIONS.map((session) => (
                        <MenuItem
                          key={session.value}
                          value={session.value}
                          disabled={now >= new Date(session.registrationClosesAt).getTime()}
                        >
                          {session.label}
                        </MenuItem>
                      ))}
                    </Field>
                    <PrivacyConsentSection
                      label="Ja, ich stimme zu und möchte mich für das Webinar anmelden"
                      isLoading={isLoading}
                    />
                    {errorMessage && (
                      <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                        {errorMessage}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <LanscoButton
                        type="submit"
                        disabled={!(isValid && dirty) || isLoading}
                        sx={{ minWidth: 180 }}
                      >
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Für Webinar anmelden'}
                      </LanscoButton>
                    </Box>
                  </Form>
                )}
              </Formik>
            </>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 300 }}>
                Vielen Dank für Deine Anmeldung!
              </Typography>
              <Typography variant="body1" component="p" sx={{ mb: 4 }}>
                Deine Anmeldung wurde erfolgreich empfangen. Lade Dir jetzt den Kalendertermin herunter, damit Du das Webinar nicht verpasst.
              </Typography>
              <LanscoButton
                // @ts-ignore - Custom variant and HTML anchor props
                variant="white"
                component="a"
                onClick={handleDownload}
                sx={{ minWidth: 220 }}
              >
                Kalendertermin herunterladen
              </LanscoButton>
            </Box>
          )}
        </FormExpirationGuard>
      </FormPaper>
      <ErrorSnackbar
        open={showErrorSnackbar}
        message={errorMessage}
        onClose={() => setShowErrorSnackbar(false)}
      />
    </>
  );
};